import { ConfirmDeleteModalComponent } from '$core/modal/confirm-delete/confirm-delete.component';
import { Pagination } from '$core/models/pagination';
import { SwalService } from '$core/shell/swal/swal.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BsModalService } from 'ngx-bootstrap/modal';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import { filter, finalize, first, switchMap } from 'rxjs/operators';
import { Blank } from '../../models/blank';
import { BlanksService } from '../../services';
import { PaginationParams } from '$shared/components/paginator/paginator.component';
import { validateLocalStoragePerPage } from '$modules/personal/modules/batch/validateLocalStorageBatch';
import { BlankStatusType } from '$modules/personal/modules/batch/features/blanks/blanks.metadata';
import { SocketService } from '$core/services/socket.service';

@Component({
  selector: 'app-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IndexComponent implements OnInit, OnDestroy {
  public items: Pagination<Blank>;
  public selected: Blank[];
  public isLoading = false;
  public queryParams;
  private isViewDestroyed = false;

  constructor(
    private readonly _modalService: BsModalService,
    private readonly _blanksService: BlanksService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _translateService: TranslateService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _swalService: SwalService,
    private readonly _toastr: ToastrService,
    private readonly socketService: SocketService
  ) {}

  public ngOnInit(): void {
    if (localStorage.getItem('batch_items_per_page')) {
      this.queryParams = {
        ...this.queryParams,
        perPage: validateLocalStoragePerPage('batch_items_per_page'),
      };
    }
    this.listenToRouter();
    this.listenToSocket();
  }

  public ngOnDestroy(): void {
    this.isViewDestroyed = true;
  }

  private listenToSocket() {
    this.socketService.events$.pipe(untilDestroyed(this)).subscribe({
      next: (res) => {
        if (res.data.type === 'document') {
          this.items.data = this.items.data.map((blank) =>
            blank.id === res.data.data.document_id
              ? { ...blank, status: res.data.data.status as BlankStatusType }
              : blank
          );
        }
        this._cd.detectChanges();
      },
    });
  }

  public pageChanged(paginationEvent: PaginationParams) {
    return this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { ...this.queryParams, ...paginationEvent },
    });
  }

  public updateSelection(): void {
    if (this.items && this.items.data) {
      this.selected = this.items.data.filter((el) => el.selected);
    }
  }

  public search(search: string | null): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        perPage: this.queryParams.perPage,
        status: this.queryParams.status,
        search,
      },
    });
  }

  public onStatusChange(status: string | null): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        perPage: this.queryParams.perPage,
        search: this.queryParams.search,
        status,
      },
    });
  }

  public downloadBlank(): void {
    const ids = this.selected.map(({ id }) => id);
    this._blanksService
      .downloadPrivateDocuments(ids)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  public remove(): void {
    const ids = this.items.data.filter((el) => el.selected).map((el) => el.id);

    this._modalService.show(ConfirmDeleteModalComponent, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
      initialState: {
        title:
          this._translateService.instant(
            'BATCH_MAILING.BLANKS_TAB.DELETE_BLANK_TITLE'
          ) + '?',
        subTitle: 'BATCH_MAILING.BLANKS_TAB.DELETE_BLANK_SUBTITLE',
        submitBtnClass: 'button--filled',
        cancelBtnClass: 'button--text',
      },
    });

    this._modalService.onHidden
      .pipe(
        first(),
        filter((res) => res === 'delete'),
        switchMap(() => this._blanksService.removeDocuments({ ids })),
        untilDestroyed(this)
      )
      .subscribe(
        () => {
          this.getItems();

          this._toastr.success(
            this._translateService.instant(
              'BATCH_MAILING.BLANKS_TAB.BLANK_DELETED'
            )
          );
        },
        (err) => {
          this.onError(err);
        }
      );
  }

  private listenToRouter(): void {
    this._route.queryParams.pipe(untilDestroyed(this)).subscribe((res) => {
      this.queryParams = {
        ...res,
        perPage: validateLocalStoragePerPage('batch_items_per_page'),
      };
      this.getItems();
    });
  }

  private getItems(): void {
    this.isLoading = true;

    this._blanksService
      .getDocuments(this.queryParams)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          if (!this.isViewDestroyed) {
            this._cd.detectChanges();
          }
        }),
        untilDestroyed(this)
      )
      .subscribe(
        (res) => {
          this.items = res;
          this.updateSelection();
        },
        (err) => {
          this.onError(err);
        }
      );
  }

  private onError(error) {
    const res: HttpErrorResponse = error;
    const swal = this._swalService.swal;
    swal.icon = 'error';
    swal.title = this._translateService.instant('AN_ERROR_HAS_OCCURRED');
    swal.text = res.error
      ? res.error.message || res.error.error
      : res.statusText;
    swal.fire();
  }
}
